import React, {useEffect, useState} from 'react';

import classNames from 'classnames';
import {observer} from 'mobx-react';
import InputMask from 'react-input-mask';
import {formatValue} from 'react-currency-input-field';

import TextField, {BaseTextFieldProps} from '@mui/material/TextField';
import {scrollToInput} from '../../helpers/scrollToInput';
import styles from './Input.module.scss';

export interface InputPropsCommon extends Omit<BaseTextFieldProps, 'error'> {
    name?: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    onFocus?: (e) => void;
    placeholder?: string;
    disabled?: boolean;
    ariaLabel?: string | 'unlabeled';
    error?: string;
    border?: boolean;
    labelRight?: string | JSX.Element;
    type?: 'email' | 'text' | 'tel';
    noLimit?: boolean;
    valueFromContext?: boolean;
    className?: string;
    label?: string;
    inputClassName?: string;
    maxValueSpecified?: number;
}

interface InputProps extends InputPropsCommon {
    date?: boolean | {month?: boolean; year?: boolean};
    mask?: string | RegExp;
    currency?: boolean;
}

const Input: React.FC<InputProps> = ({
    name,
    value,
    onChange,
    onFocus,
    placeholder,
    disabled,
    error,
    border,
    mask,
    ariaLabel,
    onBlur,
    noLimit,
    className,
    valueFromContext,
    date,
    type,
    inputMode,
    label,
    labelRight,
    currency,
    inputClassName,
    maxValueSpecified,
}) => {
    let dateMask = '99/99/9999';

    if (typeof date === 'object' && date.year && date.month) {
        dateMask = '99/9999';
    }
    if (typeof date === 'object' && date.year && !date.month) {
        dateMask = '9999';
    }

    const [valInside, setValInside] = useState<string>(value);
    useEffect(() => {
        if (date) {
            let month, day, year;
            const dateVal = valInside?.replace(/_/g, '')?.split('/') || [];
            const dayChecker = (day: string): string => (+day > 31 ? '31' : day);
            const monthChecker = (month: string): string => (+month > 12 ? '12' : month);
            const yearChecker = (year: string): string =>
                +year > new Date().getFullYear() ? `${new Date().getFullYear()}` : year;

            if (typeof date === 'object' && date.year && date.month) {
                [month, year] = dateVal;
                month = monthChecker(month);
                year = yearChecker(year);
            } else if (typeof date === 'object' && date.month) {
                [year] = dateVal;
                year = yearChecker(year);
            } else {
                [month, day, year] = dateVal;
                day = dayChecker(day);
                month = monthChecker(month);
                year = yearChecker(year);
            }
            onChange(
                [month, day, year].some((v) => v)
                    ? `${month ? month + '/' : ''}${day ? day + '/' : ''}${year ? year : ''}`
                    : '',
            );
        } else if (currency) {
            onChange(valInside.replace(/[^\d-]/g, ''));
        } else {
            onChange(valInside);
        }
    }, [valInside]);

    useEffect(() => {
        setValInside(value);
    }, [value]);

    const formattedValue = (valInside) => {
        let value = valInside.replace(/[^\d]/g, '');
        if (maxValueSpecified && Number(value) > maxValueSpecified) {
            value = maxValueSpecified.toString();
        }
        const formattedValue = formatValue({
            value,
            groupSeparator: ',',
            intlConfig: {
                locale: 'en-US',
                currency: 'USD',
            },
        });

        return maxValueSpecified && Number(value) >= maxValueSpecified ? `${formattedValue}+` : formattedValue;
    };

    const handleInputChange = (e) => {
        if (currency) {
            let inputValue = e.target.value.replace(/[^\d]/g, '');

            if (maxValueSpecified && Number(inputValue) > maxValueSpecified) {
                inputValue = maxValueSpecified.toString();
            }
            const formattedValue = formatValue({
                value: inputValue,
                groupSeparator: ',',
                intlConfig: {
                    locale: 'en-US',
                    currency: 'USD',
                },
            });

            setValInside(Number(inputValue) >= maxValueSpecified ? `${formattedValue}+` : formattedValue);
        } else {
            setValInside(e.target.value);
        }
    };

    const handleKeyDown = (e) => {
        if (currency && e.key === 'Backspace') {
            const val = e.target.value;
            if (val.includes('+') && val.endsWith('+')) {
                e.preventDefault();
                const newValue = val.slice(0, -2); // Remove the last digit and '+'
                setValInside(newValue);
            }
        }
    };

    return (
        <div
            className={classNames(
                styles.Input,
                styles.inputText,
                {
                    [styles.error]: !!error,
                    [styles.border]: !!border,
                    [styles.noLimit]: noLimit,
                },
                className,
            )}
        >
            <InputMask
                name={name}
                type={type}
                onKeyDown={handleKeyDown}
                onKeyPress={(event) => {
                    if (currency && event.code === 'Minus') {
                        event.preventDefault();
                    }
                }}
                value={valueFromContext ? value : currency ? formattedValue(valInside) : valInside}
                aria-label={ariaLabel}
                onChange={handleInputChange}
                placeholder={placeholder || ''}
                disabled={disabled}
                mask={mask || (date && dateMask)}
                maskPlaceholder=""
                onBlur={onBlur}
                onFocus={(e) => {
                    scrollToInput(e.target);
                    if (onFocus) {
                        onFocus(e);
                    }
                }}
            >
                <TextField
                    id="outlined-required"
                    label={label}
                    variant="outlined"
                    fullWidth={true}
                    className={classNames(styles.root, className, {[styles.inputLabel]: !label})}
                    placeholder={placeholder}
                    type={type}
                    inputMode={inputMode}
                    error={!!error}
                    helperText={error}
                    InputLabelProps={{
                        classes: {
                            filled: styles.filled,
                            focused: styles.focused,
                            outlined: styles.outlined,
                        },
                    }}
                    InputProps={{
                        type: type,
                        classes: {
                            notchedOutline: styles.notchedOutline,
                            input: inputClassName,
                        },
                    }}
                    FormHelperTextProps={{
                        classes: {
                            error: styles.errorMessage,
                        },
                    }}
                />
            </InputMask>
            <div className={styles.labelRight}>{typeof labelRight === 'string' ? <p>{labelRight}</p> : labelRight}</div>
        </div>
    );
};

export default observer(Input);
